import { Component, OnInit, ViewChild, TemplateRef, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { BaseService, ErrorService } from '@shared/services';
import { ResourcesService } from '@shared/services/resources.service';
import { MatSort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { first, takeUntil, tap, finalize } from 'rxjs/operators';
import { MenuItem } from 'primeng/api';
import swal from 'sweetalert2';
import { BehaviorSubject, Subject, Subscription, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { MatHeaderRow, MatHeaderRowDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '@environment/environment';

export interface FixedAssetsDto {
    fixedassetid: string;
    assetcode: string;
    name: string;
    description: string;
    icon: string;
    photo: string;
    isvalid: boolean;
    quantity: number;
}

export interface Result<T, K> {
    isSuccess: boolean;
    errorCode: number;
    message: string;
    messageType: string;
    data: T | null;
    lookupData?: K;
}

export type ExportFormat = 'excel' | 'pdf';

@Component({
    selector: 'app-fixed-assets3',
    templateUrl: './fixed-assets3.component.html',
    styleUrls: ['./fixed-assets3.component.scss']
})
export class FixedAssets3Component implements OnInit, OnDestroy {
    subscription: Subscription = new Subscription();
    private _unsubscribeAll$ = new Subject<boolean>();

    items: MenuItem[];
    loading = true;
    changeNewContant = false;
    laddaSearchProgress: boolean | number = false;

    fixedAssetsList: FixedAssetsDto[] = [];
    
    @ViewChild('exportModal', { static: true }) exportModal: TemplateRef<any>;
    @ViewChild('formAddEditModal', { static: true }) formModal: TemplateRef<any>;
    @ViewChild('importModal', { static: true }) importModal: TemplateRef<any>;
    
    modalRef: BsModalRef | null;

    @ViewChild(MatSort, { static: true }) sort: MatSort;
    // Only show Code, Name, and Status in the table (plus select checkbox)
    displayedColumns: string[] = ['select', 'assetcode', 'name', 'Status'];
    dataSource = new MatTableDataSource<FixedAssetsDto>([]);
    selection = new SelectionModel<FixedAssetsDto>(true, []);
    
    currentFixedAsset: FixedAssetsDto;
    formType = 'add';
    fixedAssetForm: FormGroup;
    imageForm: FormGroup;
    saveContant = false;
    searchValue = '';

    // Menu and action properties
    disableActivate = false;
    disableInactivate = false;
    showDialog: boolean = false;
    Gridcongid = { grid: 'FixedAssets3', windowRoute: 'FixedAssets3' };

    // Import/Export properties
    selectedExportFormat: ExportFormat = 'excel';
    selectedFile: File | null = null;
    isProcessing = false;
    importCompleted = false;
    progressPercentage = 0;
    processedItems = 0;
    totalItems = 0;
    batchSize = 50;
    importData: any[] = [];
    errorItems: any[] = [];
    validItems: any[] = [];
    insertedItems: any[] = [];
    skippedItems: any[] = [];
    private processingTimeout: any;
    private isCancelled = false;
    tamplateloading = false;

    // Filter properties
    filterList: any[] = ['Display All', 'Active', 'Inactive'];
    selectedDisplayAction: string = 'Display All';

    // Image upload properties
    fileName: string = '';
    file: File | null = null;
    previewUrl: string | ArrayBuffer | SafeResourceUrl | null = null;
    isInvalidSize: boolean = false;
    isInvalidType: boolean = false;

    // Grid configuration
    @ViewChild('fixedAssets', { read: MatTable }) fixedAssetsTable: MatTable<any>;
    @ViewChild('fixedAssetsRow') MatRow!: MatHeaderRow;

    constructor(
        private translate: TranslateService,
        public router: Router,
        public route: ActivatedRoute,
        private modalService: BsModalService,
        private fb: FormBuilder,
        private _errorService: ErrorService,
        private baseService: BaseService,
        private _resourceService: ResourcesService,
        private sanitizer: DomSanitizer
    ) {}

    ngOnInit() {
        this.initializeMenuItems();
        this.getFixedAssetsList();
        this.initForm();
        
        this.fixedAssetForm.valueChanges.pipe(takeUntil(this._unsubscribeAll$)).subscribe((changes) => {
            this.changeNewContant = true;
        });

        this.subscription.add(
            this._errorService.currentAction
                .pipe(takeUntil(this._unsubscribeAll$))
                .subscribe((action) => this.actionMsg(action))
        );
    }

    ngOnDestroy() {
        this._unsubscribeAll$.next(true);
        this._unsubscribeAll$.complete();
        if (this.modalRef) {
            this.modalRef.hide();
        }
    }

    initializeMenuItems() {
        this.items = [
            {
                label: this.translate.instant('Delete'),
                icon: 'pi pi-times',
                disabled: false,
                command: () => {
                    this.deleteSelected();
                }
            },
            {
                label: this.translate.instant('Activate'),
                icon: 'pi pi-check',
                disabled: this.disableActivate,
                command: () => {
                    this.activeSelectedFixedAssets();
                }
            },
            {
                label: this.translate.instant('Deactivate'),
                icon: 'pi pi-ban',
                disabled: this.disableInactivate,
                command: () => {
                    this.inactiveSelectedFixedAssets();
                }
            }
        ];
    }

    updateMenuItems() {
        this.items = [
            {
                label: this.translate.instant('Delete'),
                icon: 'pi pi-times',
                disabled: false,
                command: () => {
                    this.deleteSelected();
                }
            },
            {
                label: this.translate.instant('Activate'),
                icon: 'pi pi-check',
                disabled: this.disableActivate,
                command: () => {
                    this.activeSelectedFixedAssets();
                }
            },
            {
                label: this.translate.instant('Deactivate'),
                icon: 'pi pi-ban',
                disabled: this.disableInactivate,
                command: () => {
                    this.inactiveSelectedFixedAssets();
                }
            }
        ];
    }

    updateActionAvailability() {
        const selected = this.selection.selected;

        if (!selected || selected.length === 0) {
            this.disableActivate = false;
            this.disableInactivate = false;
        } else {
            const allActive = selected.every(item => item.isvalid === true);
            const allInactive = selected.every(item => item.isvalid === false);

            if (allActive) {
                this.disableActivate = true;
                this.disableInactivate = false;
            } else if (allInactive) {
                this.disableActivate = false;
                this.disableInactivate = true;
            } else {
                this.disableActivate = true;
                this.disableInactivate = true;
            }
        }
        this.updateMenuItems();
    }

    getFixedAssetsList() {
        this.searchValue = '';
        this._resourceService
            .getfixedassets()
            .pipe(takeUntil(this._unsubscribeAll$), first())
            .subscribe((result) => {
                this.fixedAssetsList = result.data;
                this.dataSource = new MatTableDataSource(this.fixedAssetsList);
                this.dataSource.sort = this.sort;
                this.loading = false;
                
                if (this.searchValue && this.searchValue.trim() !== '') {
                    this.dataSource.filter = this.searchValue.trim().toLowerCase();
                }
            });
    }

    AddNewItem() {
        this.formType = 'add';
        this.initForm();
        this.initEntityForm();
        this.openModal();
    }

    editFixedAsset(row: FixedAssetsDto) {
        this.formType = 'edit';
        this.initForm();
        this.initEntityForm();
        this.getById(row.fixedassetid);
        this.openModal();
    }

    deleteRecord(item: FixedAssetsDto) {
        const deleteRec = item.fixedassetid;
        swal.fire({
            title: 'Are you sure you want to delete this item?',
            type: "question",
            showCancelButton: true,
            confirmButtonText: "Yes",
            cancelButtonText: "No",
            confirmButtonColor: "#6FBDC1"
        }).then((result) => {
            if (result.value) {
                this._resourceService
                    .deletefixedassets([deleteRec])
                    .pipe(takeUntil(this._unsubscribeAll$), first())
                    .subscribe(
                        (result) => {
                            if (result.isSuccess) {
                                this.newMessage(54, '');
                            } else {
                                this.newMessage(55, '');
                            }
                            this.selection = new SelectionModel(true, []);
                            this.getFixedAssetsList();
                            this.changeNewContant = false;
                            this.saveContant = false;
                        },
                        (error) => {
                            // Handle error
                        }
                    );
            }
        });
    }

    deleteSelected() {
        if (this.selection.selected.length == 0) {
            this.newMessage(60, 'error');
        } else {
            this.newMessage(71, 'delete');
        }
    }

    delete() {
        const idList: string[] = this.selection.selected.map(item => item.fixedassetid.toString());
        this._resourceService
            .deletefixedassets(idList)
            .pipe(takeUntil(this._unsubscribeAll$), first())
            .subscribe(
                (result) => {
                    if (result.isSuccess) {
                        this.newMessage(54, '');
                    } else {
                        this.newMessage(55, '');
                    }
                    this.selection = new SelectionModel(true, []);
                    this.getFixedAssetsList();
                    this.changeNewContant = false;
                    this.saveContant = false;
                },
                (error) => {
                    // Handle error
                }
            );
    }

    activeSelectedFixedAssets() {
        if (this.selection.selected.length == 0) {
            this.newMessage(60, 'error');
        } else {
            let idsToActive: string[] = [];
            
            if (this.selection && this.selection.selected.length > 0) {
                const fixedAssetsIds = this.selection.selected.map(item => item.fixedassetid);
                idsToActive = [...idsToActive, ...fixedAssetsIds];
            }
            
            if (idsToActive.length > 0) {
                swal.fire({
                    title: `Are you sure you want to activate the selected items?`,
                    type: "question",
                    showCancelButton: true,
                    confirmButtonText: "Yes",
                    cancelButtonText: "No",
                    confirmButtonColor: "#6FBDC1"
                }).then((result) => {
                    if (result.value) {
                        this._resourceService
                            .activefixedassets(idsToActive)
                            .pipe(takeUntil(this._unsubscribeAll$), first())
                            .subscribe(
                                (result) => {
                                    if (result.isSuccess) {
                                        this.newMessage(56, '');
                                    } else {
                                        this.newMessage(55, '');
                                    }
                                    
                                    this.selection = new SelectionModel(true, []);
                                    this.getFixedAssetsList();
                                    this.laddaSearchProgress = false;
                                    this.changeNewContant = false;
                                    this.saveContant = false;
                                },
                                (error) => {
                                    this.laddaSearchProgress = false;
                                }
                            );
                    }
                });
            }
        }
    }

    inactiveSelectedFixedAssets() {
        if (this.selection.selected.length == 0) {
            this.newMessage(60, 'error');
        } else {
            let idsToInactive: string[] = [];
            
            if (this.selection && this.selection.selected.length > 0) {
                const fixedAssetsIds = this.selection.selected.map(item => item.fixedassetid);
                idsToInactive = [...idsToInactive, ...fixedAssetsIds];
            }
            
            if (idsToInactive.length > 0) {
                swal.fire({
                    title: `Are you sure you want to deactivate the selected items?`,
                    type: "question",
                    showCancelButton: true,
                    confirmButtonText: "Yes",
                    cancelButtonText: "No",
                    confirmButtonColor: "#6FBDC1"
                }).then((result) => {
                    if (result.value) {
                        this._resourceService
                            .Inactivefixedassets(idsToInactive)
                            .pipe(takeUntil(this._unsubscribeAll$), first())
                            .subscribe(
                                (result) => {
                                    if (result.isSuccess) {
                                        this.newMessage(56, '');
                                    } else {
                                        this.newMessage(55, '');
                                    }
                                    
                                    this.selection = new SelectionModel(true, []);
                                    this.getFixedAssetsList();
                                    this.laddaSearchProgress = false;
                                    this.changeNewContant = false;
                                    this.saveContant = false;
                                },
                                (error) => {
                                    this.laddaSearchProgress = false;
                                }
                            );
                    }
                });
            }
        }
    }

    getById(fixedAssetId: string) {
        this._resourceService
            .getfixedassetbyid(fixedAssetId)
            .pipe(takeUntil(this._unsubscribeAll$), first())
            .subscribe(
                (result) => {
                    this.currentFixedAsset = result.data;
                    const controls = this.fixedAssetForm.controls;
                    Object.keys(controls).forEach((key) => {
                        if (this.currentFixedAsset[key] != null) {
                            this.fixedAssetForm.get(key).setValue(this.currentFixedAsset[key]);
                        }
                        this.changeNewContant = false;
                    });
                    
                    // Set preview URL if photo exists
                    if (this.currentFixedAsset.photo) {
                        this.previewUrl = this.currentFixedAsset.photo;
                    }
                },
                (error) => {}
            );
    }

    openModal() {
        const configs = {
            keyboard: false,
            ignoreBackdropClick: true,
            class: ' modal-lg modal-primary ModelPopUpLayoutFilterPopUps commenModel animated',
            animated: false
        };
        this.modalRef = this.modalService.show(this.formModal, configs);
    }

    openAnyModal(modal: TemplateRef<any>) {
        const configs = {
            keyboard: false,
            ignoreBackdropClick: true,
            class: ' modal-lg modal-primary ModelPopUpLayoutFilterPopUps commenModel animated',
            animated: false
        };
        this.modalRef = this.modalService.show(modal, configs);
    }

    close() {
        if (!this.changeNewContant) {
            this.modalRef.hide();
        } else {
            this.newMessage(144, 'ClosePopup');
        }
    }

    ClosePopup() {
        this.modalRef.hide();
        this.changeNewContant = false;
    }

    isAllSelected() {
        let numSelected = 0;
        if (this.selection.selected) {
            numSelected = this.selection.selected.length;
        }
        const numRows = this.dataSource.data.length;
        return numSelected === numRows;
    }

    masterToggle() {
        if (this.isAllSelected()) {
            this.selection.clear();
        } else {
            this.selection.select(...this.dataSource.data);
        }
    }

    checkboxLabel(row?: FixedAssetsDto): string {
        if (!row) {
            return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.assetcode}`;
    }

    applyFilter(filterValue: string) {
        this.dataSource.filter = filterValue.trim().toLowerCase();
    }

    onFilterChange(filterValue: string) {
        if (!filterValue || filterValue === 'Display All') {
            this.selectedDisplayAction = 'Display All';
            this.dataSource.data = [...this.fixedAssetsList];
        } else if (filterValue === 'Active') {
            this.selectedDisplayAction = 'Active';
            this.dataSource.data = this.fixedAssetsList.filter(item => item.isvalid === true);
        } else if (filterValue === 'Inactive') {
            this.selectedDisplayAction = 'Inactive';
            this.dataSource.data = this.fixedAssetsList.filter(item => item.isvalid === false);
        }
    }

    private initForm(): void {
        this.fixedAssetForm = this.fb.group({
            fixedassetid: new FormControl(null),
            assetcode: new FormControl('', [Validators.required, Validators.maxLength(50)]),
            name: new FormControl('', [Validators.required, Validators.maxLength(100)]),
            description: new FormControl('', [Validators.maxLength(500)]),
            icon: new FormControl(null),
            photo: new FormControl(null),
            isvalid: new FormControl(true),
            // quantity kept in model but not exposed in the UI as per requirements
            quantity: new FormControl(1, [Validators.required, Validators.min(0)])
        });
    }

    initEntityForm() {
        this.imageForm = this.fb.group({
            imageName: ['', Validators.required],
            imageFile: [null, [Validators.required, this.validateFileType, this.validateFileSize]]
        });
    }

    saveAddEdit() {
        this.saveContant = true;
        this.laddaSearchProgress = 1;
        const controls = this.fixedAssetForm.controls;
        Object.keys(controls).forEach((key) => {
            controls[key].markAsTouched();
        });

        if (this.fixedAssetForm.status === 'VALID') {
            if (this.fixedAssetForm.value.fixedassetid == null) {
                // Add Service
                this._resourceService
                    .addfixedasset(this.fixedAssetForm.value)
                    .pipe(
                        takeUntil(this._unsubscribeAll$),
                        first(),
                        finalize(() => (this.laddaSearchProgress = false))
                    )
                    .subscribe((result) => {
                        this.saveContant = false;
                        if (result.isSuccess) {
                            this.newMessage(93, '');
                            this.changeNewContant = false;
                            this.close();
                        } else {
                            this.newMessage(77, '');
                            this.changeNewContant = false;
                        }
                        this.getFixedAssetsList();
                    });
            } else {
                // Edit Service
                this._resourceService
                    .editfixedasset(this.fixedAssetForm.value)
                    .pipe(
                        takeUntil(this._unsubscribeAll$),
                        first(),
                        finalize(() => (this.laddaSearchProgress = false))
                    )
                    .subscribe((result) => {
                        if (result.isSuccess) {
                            this.newMessage(4, '');
                            this.changeNewContant = false;
                            this.close();
                        } else {
                            this.newMessage(77, '');
                            this.changeNewContant = false;
                        }
                        this.getFixedAssetsList();
                        this.saveContant = false;
                    });
            }
        } else {
            this.saveContant = true;
            window.scrollTo(0, 0);
            this.laddaSearchProgress = false;
            this.changeNewContant = false;
            this.newMessage(3, '');
        }
    }

    // Image upload methods
    onFileChange(event: any) {
        const file = event.target.files[0];
        this.isInvalidSize = false;
        this.isInvalidType = false;

        if (file) {
            this.isInvalidSize = !this.validateFileSize(file);
            this.isInvalidType = !this.validateFileType(file);

            if (!this.isInvalidSize && !this.isInvalidType) {
                this.file = file;
                this.fileName = file.name;
                this.imageForm.patchValue({ imageFile: file });

                const reader = new FileReader();
                reader.onload = () => {
                    const unsafeUrl = reader.result as string;
                    this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(unsafeUrl);
                    this.imageForm.get('imageName')?.setValue(this.fileName);
                };
                reader.readAsDataURL(file);
            } else {
                this.file = null;
                this.fileName = '';
                this.previewUrl = null;
                this.imageForm.get('imageName')?.setValue('');
                this.fixedAssetForm.patchValue({ photo: null });
            }
        }
    }

    validateFileType(file: File) {
        const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
        return allowedTypes.includes(file.type);
    }

    validateFileSize(file: File) {
        return file.size <= 2 * 1024 * 1024; // 2 MB limit
    }

    removeFile() {
        this.file = null;
        this.fileName = '';
        this.previewUrl = null;
        this.imageForm.patchValue({ imageFile: null });
        this.imageForm.get('imageName')?.setValue('');
        this.fixedAssetForm.patchValue({ photo: null });
    }

    onSubmit() {
        if (this.imageForm.valid && this.file) {
            const formData = new FormData();
            const newFileName = this.imageForm.get('imageName')?.value || this.file.name;
            const renamedFile = new File([this.file], newFileName, { type: this.file.type });

            formData.append('imageName', newFileName);
            formData.append('file', renamedFile);

            this.uploadFile(formData, newFileName);
        }
    }

    uploadFile(formData: FormData, fileName: string): Observable<any> {
        return this.baseService.uploadImg(formData).pipe(
            tap((response) => {
                const mediaUrl = environment.AttachmentUrl + response.fileName;
                this.fixedAssetForm.get('photo')?.setValue(mediaUrl);
                this.previewUrl = mediaUrl;
            })
        );
    }

    // Export functionality
    async Export(format: ExportFormat = 'excel'): Promise<void> {
        const dataToExport = this.dataSource.filteredData;
        // Export only the requested fields like Amenities: Name, Code, Status
        const exportData = dataToExport.map(item => ({
            Name: item.name,
            Code: item.assetcode,
            Status: item.isvalid ? 'Active' : 'Inactive'
        }));

        if (format === 'excel') {
            await this.exportToExcel(exportData);
        } else if (format === 'pdf') {
            await this.exportToPDF(exportData);
        }
    }

    private async exportToExcel(data: any[]): Promise<void> {
        const fileName = 'Fixed Assets.xlsx';
        
        const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
        const workbook: XLSX.WorkBook = {
            Sheets: { data: worksheet },
            SheetNames: ['data']
        };
        
        const excelBuffer: any = XLSX.write(workbook, {
            bookType: 'xlsx',
            type: 'array'
        });
        
        const blob: Blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
        });
        
        await this.saveFile(blob, fileName, 'Excel files', {
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
        });
    }

    private async exportToPDF(data: any[]): Promise<void> {
        const fileName = 'Fixed Assets.pdf';
        
        const doc = new jsPDF();
        
        doc.setFontSize(16);
        doc.text('Fixed Assets Exported Data Report', 14, 20);
        
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);
        
        const tableColumns = ['Name', 'Code', 'Status'];
        const tableRows = data.map(item => [
            item['Name'],
            item['Code'],
            item['Status']
        ]);
        
        autoTable(doc, {
            head: [tableColumns],
            body: tableRows,
            startY: 40,
            styles: {
                fontSize: 9,
                cellPadding: 3,
            },
            headStyles: {
                fillColor: [63, 81, 181],
                textColor: 255,
                fontStyle: 'bold'
            },
            alternateRowStyles: {
                fillColor: [245, 245, 245]
            },
            margin: { top: 40 }
        });
        
        const pdfBlob = doc.output('blob');
        await this.saveFile(pdfBlob, fileName, 'PDF files', {
            'application/pdf': ['.pdf']
        });
    }

    private async saveFile(
        blob: Blob,
        fileName: string,
        description: string,
        accept: Record<string, string[]>
    ): Promise<void> {
        if ('showSaveFilePicker' in window) {
            try {
                const fileHandle = await (window as any).showSaveFilePicker({
                    suggestedName: fileName,
                    types: [{
                        description: description,
                        accept: accept
                    }]
                });
                
                const writable = await fileHandle.createWritable();
                await writable.write(blob);
                await writable.close();
                this.ClosePopup();
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error saving file:', error);
                    FileSaver.saveAs(blob, fileName);
                }
            }
        } else {
            FileSaver.saveAs(blob, fileName);
            this.ClosePopup();
        }
    }

    // Import functionality
    openImportModal() {
        this.resetImportState();
        const configs = {
            keyboard: false,
            ignoreBackdropClick: true,
            class: 'modal-lg modal-primary ModelPopUpLayoutFilterPopUps commenModel animated',
            animated: false
        };
        this.modalRef = this.modalService.show(this.importModal, configs);
    }

    resetImportState(): void {
        this.selectedFile = null;
        this.isProcessing = false;
        this.importCompleted = false;
        this.progressPercentage = 0;
        this.processedItems = 0;
        this.totalItems = 0;
        this.importData = [];
        this.errorItems = [];
        this.validItems = [];
        this.isCancelled = false;
    }

    onFileSelected(event: any): void {
        const file: File = event.target.files[0];
        if (file) {
            this.selectedFile = file;
            const reader: FileReader = new FileReader();
            reader.onload = (e: any) => {
                try {
                    const data: Uint8Array = new Uint8Array(e.target.result);
                    const workbook: XLSX.WorkBook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const rawData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
                    // Map import: assetcode from 'Code', name from 'Name', isvalid from 'Status', no quantity or description
                    this.importData = (rawData as any[]).map((item, index) => ({
                        fixedassetid: null,
                        assetcode: item["Code"] || '',
                        name: item["Name"] || '',
                        description: '',
                        icon: null,
                        photo: null,
                        isvalid: (item["Status"]?.toLowerCase() === 'active'),
                        _rowIndex: index + 2
                    }));
                    this.validateData();
                } catch (error) {
                    this.selectedFile = null;
                }
            };
            reader.readAsArrayBuffer(file);
        }
    }

    getFileSizeDisplay(): string {
        if (!this.selectedFile) return '';
        
        const fileSizeKB = this.selectedFile.size / 1024;
        if (fileSizeKB < 1024) {
            return `${fileSizeKB.toFixed(2)} KB`;
        } else {
            return `${(fileSizeKB / 1024).toFixed(2)} MB`;
        }
    }

    validateData(): void {
        this.errorItems = [];
        this.validItems = [];
        this.importData.forEach((item: any) => {
            let isValid = true;
            const rowError: any = {
                rowNumber: item._rowIndex,
                name: item.name,
                error: ''
            };
            if (!item.assetcode) {
                rowError.error += 'Code is required. ';
                isValid = false;
            }
            if (!item.name) {
                rowError.error += 'Name is required. ';
                isValid = false;
            }
            if (item.name && item.name.length > 100) {
                rowError.error += 'Name max length is 100 characters. ';
                isValid = false;
            }
            if (item.assetcode && item.assetcode.length > 50) {
                rowError.error += 'Code max length is 50 characters. ';
                isValid = false;
            }
            // No description or quantity validation
            if (isValid) {
                this.validItems.push(item);
            } else {
                this.errorItems.push(rowError);
            }
        });
    }

    startImport(): void {
        if (this.importData.length === 0) {
            return;
        }

        if (this.validItems.length === 0) {
            this.newMessage(229, '');
            return;
        }
        
        const dataToImport = this.errorItems.length > 0 ? this.validItems : this.importData;
        this.totalItems = dataToImport.length;
        this.processedItems = 0;
        this.progressPercentage = 0;
        this.isProcessing = true;
        this.isCancelled = false;
        
        this.processBatch(dataToImport, 0);
    }

    saveFixedAssetsBatch(batch: any[]): Promise<Result<any[], any>> {
        return new Promise((resolve, reject) => {
            this._resourceService.addfixedassetbatch(batch).subscribe({
                next: (response) => {
                    this.insertedItems = response.data.inserted || [];
                    this.skippedItems = response.data.skipped || [];

                    if (this.dataSource && this.dataSource.data && this.insertedItems.length > 0) {
                        this.dataSource.data = [...this.dataSource.data, ...this.insertedItems];
                    }

                    this.importCompleted = true;
                    resolve(response);
                },
                error: (err) => reject(err)
            });
        });
    }

    processBatch(data: any[], startIndex: number): void {
        if (this.isCancelled) return;

        const endIndex = Math.min(startIndex + this.batchSize, data.length);
        const batch = data.slice(startIndex, endIndex);

        this.saveFixedAssetsBatch(batch).then((response) => {
            this.processedItems += response?.data?.length || 0;
            this.progressPercentage = Math.round((this.processedItems / this.totalItems) * 100);

            if (endIndex < data.length && !this.isCancelled) {
                this.processingTimeout = setTimeout(() => {
                    this.processBatch(data, endIndex);
                }, 100);
            } else if (!this.isCancelled) {
                this.completeImport();
            }
        }).catch((error) => {
            swal.fire('Import Error', 'Error saving batch: ' + error.message, 'error');
            this.isProcessing = false;
        });
    }

    continueWithValidItems(): void {
        if (this.validItems.length === 0) {
            return;
        }
        this.startImport();
    }

    cancelImport(): void {
        this.isCancelled = true;
        if (this.processingTimeout) {
            clearTimeout(this.processingTimeout);
        }
        this.isProcessing = false;
    }

    completeImport(): void {
        this.isProcessing = false;
        this.importCompleted = true;
    }

    closeImportModal(): void {
        if (this.isProcessing) {
            if (confirm('Import is in progress. Are you sure you want to cancel?')) {
                this.cancelImport();
            } else {
                return;
            }
        }
        this.modalRef?.hide();
        this.resetImportState();
    }

    downloadTemplate() {
        this.tamplateloading = true;
        this._resourceService
            .downloadAmenitiesTemplate()
            .pipe(takeUntil(this._unsubscribeAll$), first())
            .subscribe((blob: Blob) => {
                const link = document.createElement('a');
                const url = window.URL.createObjectURL(blob);
                link.href = url;
                link.download = 'FixedAssetsTemplate.xlsx';
                link.click();
                window.URL.revokeObjectURL(url);
                this.tamplateloading = false;
            });
    }

    // Grid configuration methods
    toggleGridPopup(): void {
        this.showDialog = !this.showDialog;
    }

    ngAfterViewInit() {
        this.getTableConfig();
    }

    getTableConfig() {
        this.baseService
            .post(`GridConfig/GetGridCongif`, this.Gridcongid)
            .pipe(takeUntil(this._unsubscribeAll$), first())
            .subscribe((result) => {
                if (result.data != null && result.data.length > 0) {
                    // Limit grid config to allowed columns only
                    const allowed = new Set(['assetcode', 'name', 'Status']);
                    let list = result.data.filter((obj) => allowed.has(obj.col));
                    this.MatRow = ['select', ...list.map((obj) => obj.col)];
                    
                    if (this.fixedAssetsTable && this.fixedAssetsTable._contentHeaderRowDefs?.length) {
                        const cols = ['select', ...list.map((obj) => obj.col)];
                        this.fixedAssetsTable._contentHeaderRowDefs.toArray()[0].columns = cols;
                        this.fixedAssetsTable._contentRowDefs.toArray()[0].columns = cols;
                        this.fixedAssetsTable.renderRows();
                    }
                }
            });
    }

    updateNewCol(event) {
        if (event) {
            // Enforce allowed columns only
            const allowed = new Set(['assetcode', 'name', 'Status']);
            let visibleCols = event.filter((e) => e.isShowen === true && e.col !== 'select' && allowed.has(e.col));
            let colKeys = ['select', ...visibleCols.map((obj) => obj.col)];

            this.MatRow = colKeys;

            if (this.fixedAssetsTable?._contentHeaderRowDefs?.length) {
                this.fixedAssetsTable._contentHeaderRowDefs.toArray()[0].columns = colKeys;
            }

            if (this.fixedAssetsTable?._contentRowDefs?.length) {
                this.fixedAssetsTable._contentRowDefs.toArray()[0].columns = colKeys;
            }

            this.fixedAssetsTable.renderRows();
        }
    }

    actionMsg(data) {
        if (data !== '') {
            this[data]();
            this._errorService.action('');
        }
    }

    newMessage(id, action) {
        this._errorService.changeMessage({ id: id, action: action });
    }
}